import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSiteData } from '../context/SiteDataContext';
import { useLanguage } from '../context/LanguageContext';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { siteData } = useSiteData();
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isMenuOpen && !target.closest('header')) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
      return;
    }
    
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(t('whatsapp.emergency'));
    const whatsappUrl = `https://wa.me/${siteData.contactInfo.whatsapp?.replace(/\D/g, '')}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  const toggleLanguage = () => {
    setLanguage(language === 'tr' ? 'en' : 'tr');
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 shadow-md font-sans ${
      isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-md'
    }`}>
      {/* Emergency Bar */}
      <div className="bg-red-600 text-white py-2 px-4">
        <div className="container mx-auto flex items-center justify-between text-sm">
          <div className="flex items-center space-x-2 md:space-x-4 min-w-0">
            <span className="font-semibold text-xs md:text-sm truncate">{t('nav.emergency')}</span>
            <span className="hidden sm:inline text-xs md:text-sm">|</span>
            <span className="hidden sm:inline text-xs md:text-sm">{siteData.contactInfo.phone}</span>
          </div>
          <div className="flex items-center space-x-1 md:space-x-2 flex-shrink-0">
            <button
              onClick={() => window.open(`tel:${siteData.contactInfo.phone}`, '_self')}
              className="flex items-center bg-white text-red-600 px-2 md:px-3 py-1 rounded-full font-semibold shadow hover:bg-red-50 transition-colors gap-1 text-xs md:text-sm min-h-[32px] focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              aria-label={t('nav.call')}
            >
              <span className="text-sm md:text-lg">📞</span>
              <span className="hidden xs:inline">{t('nav.call')}</span>
            </button>
            <button
              onClick={handleWhatsAppClick}
              className="flex items-center bg-green-500 text-white px-2 md:px-3 py-1 rounded-full font-semibold shadow hover:bg-green-600 transition-colors gap-1 text-xs md:text-sm min-h-[32px] focus:outline-none focus-visible:ring-2 focus-visible:ring-green-300"
              aria-label={t('nav.whatsapp')}
            >
              <span className="text-sm md:text-lg">💬</span>
              <span className="hidden xs:inline">{t('nav.whatsapp')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="container mx-auto px-4 py-4 md:py-6 font-sans">
        <div className="flex items-center justify-between">
          {/* Logo - Improved mobile responsiveness */}
          <Link to="/" className="flex items-center space-x-2 md:space-x-4 group font-sans min-w-0 flex-1 md:flex-none">
            <img 
              src="/logo.svg" 
              alt="AKC Logo" 
              className="w-10 h-10 md:w-14 md:h-14 rounded-xl object-contain bg-white border border-gray-200 shadow-sm transition-transform flex-shrink-0" 
            />
            <div className="flex flex-col justify-center min-w-0">
              <span className="text-lg md:text-2xl font-extrabold text-gray-900 leading-tight tracking-tight truncate">
                AKC Mekanik İnşaat
              </span>
              <span className="text-xs text-gray-500 font-medium mt-1 tracking-wide hidden sm:block truncate">
                Güvenilir Hasar Onarım Uzmanı
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8 xl:space-x-10 ml-8">
            {[
              { id: 'home', label: t('nav.home') },
              { id: 'about', label: t('nav.about') },
              { id: 'services', label: t('nav.services') },
              { id: 'references', label: t('nav.references') },
              { id: 'contact', label: t('nav.contact') },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`relative text-gray-700 font-semibold px-2 py-1 transition-colors duration-200 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded
                  ${location.hash === `#${item.id}` || (location.pathname === '/' && item.id === 'home' && location.hash === '') ? 'text-blue-700' : ''}
                `}
              >
                <span>{item.label}</span>
                <span className={`absolute left-0 -bottom-1 w-full h-0.5 bg-blue-600 transition-transform origin-left duration-200 ${
                  location.hash === `#${item.id}` || (location.pathname === '/' && item.id === 'home' && location.hash === '') ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                }`} />
              </button>
            ))}
            
            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-2 text-gray-700 font-semibold border border-gray-300 rounded-full px-4 py-2 ml-4 shadow-sm hover:border-blue-600 hover:text-blue-700 transition-colors bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              aria-label={`Switch to ${language === 'tr' ? 'English' : 'Turkish'}`}
            >
              <span className="text-lg">{language === 'tr' ? '🇹🇷' : '🇺🇸'}</span>
              <span className="text-sm font-bold">{language === 'tr' ? 'TR' : 'EN'}</span>
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {/* Mobile Menu Controls */}
          <div className="lg:hidden flex items-center space-x-2 flex-shrink-0">
            {/* Mobile Language Switcher */}
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-1 text-gray-700 hover:text-blue-600 transition-colors border border-gray-300 rounded-full px-2 md:px-3 py-1 shadow-sm bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              aria-label={`Switch to ${language === 'tr' ? 'English' : 'Turkish'}`}
            >
              <span className="text-sm">{language === 'tr' ? '🇹🇷' : '🇺🇸'}</span>
              <span className="text-xs font-semibold">{language === 'tr' ? 'TR' : 'EN'}</span>
            </button>
            
            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-blue-600 transition-colors p-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              aria-label="Toggle menu"
              aria-expanded={isMenuOpen}
            >
              <svg className="w-6 h-6 md:w-7 md:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu - Improved design and accessibility */}
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden" onClick={() => setIsMenuOpen(false)} />
            
            {/* Menu Panel */}
            <div className="fixed top-full left-0 right-0 bg-white shadow-2xl z-50 lg:hidden border-t border-gray-200">
              <nav className="py-6 px-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                <div className="flex flex-col space-y-1">
                  {[
                    { id: 'home', label: t('nav.home'), icon: '🏠' },
                    { id: 'about', label: t('nav.about'), icon: 'ℹ️' },
                    { id: 'services', label: t('nav.services'), icon: '🛠️' },
                    { id: 'references', label: t('nav.references'), icon: '🏢' },
                    { id: 'contact', label: t('nav.contact'), icon: '📞' },
                  ].map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className="flex items-center text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-colors font-semibold text-left px-4 py-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-blue-50"
                    >
                      <span className="text-lg mr-3">{item.icon}</span>
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
                
                {/* Mobile Menu Footer */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex flex-col space-y-3">
                    <a
                      href={`tel:${siteData.contactInfo.phone}`}
                      className="flex items-center justify-center bg-red-600 text-white px-6 py-3 rounded-xl font-semibold shadow hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      <span className="text-lg mr-2">📞</span>
                      Acil Arama
                    </a>
                    <button
                      onClick={handleWhatsAppClick}
                      className="flex items-center justify-center bg-green-500 text-white px-6 py-3 rounded-xl font-semibold shadow hover:bg-green-600 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <span className="text-lg mr-2">💬</span>
                      WhatsApp
                    </button>
                  </div>
                </div>
              </nav>
            </div>
          </>
        )}
      </nav>
    </header>
  );
};

export default Header;
